import { useCallback, useMemo } from "react";
import _ from "lodash";

import { DeleteOutlineOutlinedIcon, FunctionsIcon } from "icons";
import { colors } from "theme/palette";
import { copyTextToClipboard } from "utils/common";

import Button from "components/Button";
import IconButton from "components/IconButton";
import Stack from "components/Stack";
import { IRenderCell } from "components/StyledTable/utils/header";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import CellTitle from "./components/CellTitle";

const NameCell: IRenderCell<
  {
    label: string;
    isTotalRow: boolean;
    infoText: string;
    isCalculationItem: boolean;
    isAdded: boolean;
    itemCode: string;
    isBold?: boolean;
  },
  {
    setCollapsedSubCategories: React.Dispatch<
      React.SetStateAction<Array<string>>
    >;
    collapsedSubCategories: string[];
    editMode: boolean;
    handleRowDelete: (args: { rowKey: string }) => void;
    isDeleted: boolean;
  }
> = ({
  itemKey,
  row,
  index,
  Wrapper,
  setCollapsedSubCategories,
  collapsedSubCategories,
  editMode,
  handleRowDelete,
  isDeleted,
}) => {
  const { show: showConfirmationDialog } = useConfirmationDialog();

  const handleDelete = () => {
    showConfirmationDialog({
      onConfirm: () => handleRowDelete({ rowKey: row.itemKey }),
      confirmButtonText: "Delete",
      message:
        "Are you sure you want to delete this row and all of its contents?",
    });
  };

  const color = useMemo(() => {
    if (isDeleted) {
      return colors.red40;
    }

    if (row.isAdded) {
      return colors.green40;
    }

    return colors.gray10;
  }, [isDeleted, row.isAdded]);

  const cellDataTestId = useMemo(
    () =>
      row.isTotalRow ? "total-subcategory-cell" : "item-name-subcategory-cell",
    [row.isTotalRow]
  );

  const handleCodeClick = useCallback(({ code }: { code: string }) => {
    copyTextToClipboard({ text: code });
    toast.successMessage("Code has been copied to the clipboard.");
  }, []);

  const infoText = useMemo(
    () =>
      !_.isNil(row.infoText) && !_.isEmpty(row.infoText)
        ? row.infoText
        : undefined,
    [row.infoText]
  );

  return (
    <Wrapper
      style={{
        padding: `5px 16px 5px 15px`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexGrow: 0,
        width: "350px",
        height: "40px",
        borderBottom: `1px solid ${colors.blue60}`,
        backgroundColor: color,
      }}
    >
      {row.isTotalRow || row.isCalculationItem ? (
        <Stack direction="row" alignItems="center" gap={0.8} maxWidth="100%">
          <CellTitle
            cellDataTestId={cellDataTestId}
            setCollapsedSubCategories={setCollapsedSubCategories}
            isTotalRow={row.isTotalRow}
            collapsedSubCategories={collapsedSubCategories}
            label={row.label}
            isBold={!_.isNil(row.isBold) ? row.isBold : false}
            isCalculationItem={row.isCalculationItem}
          />
          {row.isTotalRow && (
            <Tooltip
              placement="top"
              title={
                !_.isNil(infoText)
                  ? infoText
                  : `Sum of all line items in ${row.label}.`
              }
            >
              <FunctionsIcon
                sx={{
                  width: 16,
                  height: 16,
                  color: colors.gray60,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}
          {row.isCalculationItem && !_.isNil(infoText) && (
            <Tooltip title={infoText}>
              <Text
                variant="text4"
                fontWeight={600}
                sx={{ color: colors.gray60, cursor: "pointer" }}
              >
                ƒ
              </Text>
            </Tooltip>
          )}
        </Stack>
      ) : (
        <CellTitle
          cellDataTestId={cellDataTestId}
          setCollapsedSubCategories={setCollapsedSubCategories}
          isTotalRow={row.isTotalRow}
          collapsedSubCategories={collapsedSubCategories}
          label={row.label}
          isBold={!_.isNil(row.isBold) ? row.isBold : false}
          isCalculationItem={row.isCalculationItem}
        />
      )}

      {!isDeleted && editMode && !row.isTotalRow && !row.isCalculationItem && (
        <IconButton
          size="small"
          component="span"
          title="Delete row"
          onClick={handleDelete}
          dataTestid="delete-row-data-button"
          sx={{ marginRight: 1 }}
        >
          <DeleteOutlineOutlinedIcon sx={{ width: 20, height: 20 }} />
        </IconButton>
      )}
      {!row.isTotalRow && row.itemCode && (
        <Tooltip title={row.itemCode} enterDelay={1000} enterNextDelay={1000}>
          <Button
            color="secondary"
            size="small"
            data-testid="item-code-tag"
            sx={{ margin: 0, flexShrink: 0, maxWidth: "150px" }}
            onClick={() => handleCodeClick({ code: row.itemCode })}
          >
            <Text
              sx={{
                pointerEvents: "none",
                fontSize: "11px",
                textTransform: "uppercase",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.itemCode}
            </Text>
          </Button>
        </Tooltip>
      )}
    </Wrapper>
  );
};
export default NameCell;
